<template>
  <div class="mt-4 mt-lg-0">
    <page-header
      :page="headerPage"
      :actions="headerActions"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />

    <board class="mb-4" :details="boardDetails">
      <template #data-item="{ item }">
        <div v-if="!item.data">
          <custom-balloon
            :background-color="item.statusColor"
            :text="item.statusName"
            dot
          />
        </div>
      </template>
    </board>

    <div class="mb-4 row">
      <custom-tab
        :default-active-tab="activeTab"
        class="w-100"
        nav-class="mx-0 nav-vue-underline"
      >
        <custom-tab-item v-if="showEquipments" title="Equipos">
          <referral-equipments
            :referral="referral"
            :is-read-only="isReadOnly"
            @loaded="onEquipmentsLoaded"
          />
        </custom-tab-item>
        <custom-tab-item title="Servicios" v-if="showServices">
          <referral-services
            ref="referral-services"
            :referral="referral"
            :validate="validate"
            :is-read-only="isReadOnly"
            :classifications="classifications"
            :purchase-methods="purchaseMethods"
            :documentation-methods="serviceDocumentationMethods"
            :is-inventory-flow-enabled="isInventoryFlowEnabled"
            @service-saved="onSaveServiceDocumentation"
          />
        </custom-tab-item>
      </custom-tab>
    </div>
  </div>
</template>
<script>
import PageHeader from 'PageHeader';
import Board from 'Board';
import CustomBalloon from 'custom-balloon';
import { CustomTab, CustomTabItem } from 'custom-tabs';

import mixAlert from '@/mixins/alert.mixin';
import { date } from '@/utils/filters';

import ReferralEquipments from './components/ReferralEquipments.vue';
import ReferralServices from './components/ReferralServices.vue';

import referralService from '@/services/api/referralsandequipments.api';
import PeiAtReferralTypes from '@/utils/constants/peiAtReferralType';
import PurchaseMethods from '@/utils/constants/purchaseMethods';
import PeiAtReferralTypeTabs from '@/utils/constants/peiAtReferralTypeTabs';
import { getSysparameterByNames } from '@/services/api/sysparameter.api';

const REFERRAL_EQUIPMENT_ROUTE_NAME = 'referralsandequipment';

export default {
  name: 'ReferralDetails',
  components: {
    PageHeader,
    Board,
    CustomBalloon,
    CustomTab,
    CustomTabItem,
    ReferralEquipments,
    ReferralServices,
  },
  mixins: [mixAlert],
  data: () => ({
    referral: {},
    validate: false,
    activeTab: 0,
    showEquipments: true,
    classifications: [],
    purchaseMethods: [],
    serviceDocumentationMethods: [],
    servicesValidatorRef: null,
    serviceAreSaved: false,
    isInventoryFlowEnabled: false,
  }),
  computed: {
    referralId() {
      return Number(this.$route.params.id);
    },
    type() {
      return Number(this.$route.params.type);
    },
    headerPage() {
      return {
        icon: 'fas fa-list',
        title: 'Detalles del Referido',
        routes: [
          'Asistencia Tecnológica',
          'Manejo de Referidos y Equipos',
          'Detalles del Referido',
        ],
      };
    },
    headerActions() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () =>
            this.$router.push({ name: REFERRAL_EQUIPMENT_ROUTE_NAME }),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'success',
          visible:
            !this.isReadOnly && this.type === PeiAtReferralTypeTabs.Services,
          click: () => this.handleSave(),
        },
      ];
    },
    boardDetails() {
      return [
        [
          {
            label: 'Estudiante:',
            icon: 'fas fa-user',
            data: `${this.referral.studentName} - ${this.referral.studentSie}`,
            cols: 'col-12 col-md-3 pt-2',
          },
          {
            label: 'Número de Referido:',
            icon: 'fas fa-file-user',
            data: this.referral.referralNumber || '-',
            cols: 'col-12 col-md pt-2',
          },
          {
            label: 'Fecha de Referido:',
            icon: 'fas fa-calendar-minus',
            data: date(this.referral.referralDate),
            cols: 'col-12 col-md pt-2',
          },
          {
            label: 'CAAT:',
            icon: 'fas fa-location-dot',
            data: this.referral.caat || '-',
            cols: 'col-12 col-md pt-2',
          },
          {
            label: 'Estatus:',
            icon: 'fas fa-file-lines',
            statusColor: this.referral.serviceStatusColor,
            statusName: this.referral.serviceStatusName,
            cols: 'col-12 col-md-3 pt-2',
          },
        ],
      ];
    },
    isReadOnly() {
      return this.serviceAreSaved;
    },
    showServices() {
      const validTypes = [
        PeiAtReferralTypes.EquipmentRelatedServiceRequest,
        PeiAtReferralTypes.TechnicalAssistanceServiceRequest,
      ];

      return (
        validTypes.includes(this.referral.referralTypeId) &&
        this.type === PeiAtReferralTypeTabs.Services
      );
    },
  },
  watch: {
    'referral.services': {
      handler: 'setReadOnly',
      immediate: true,
    },
  },
  async mounted() {
    await this.initData();
  },
  methods: {
    async initData() {
      await Promise.all([
        this.getInventoryParam(),
        this.getBoardInfo(),
        this.getClassifications(),
        this.getPurchaseMethods(),
        this.getServiceDocumentationMethods(),
      ]);
    },
    async getInventoryParam() {
      const { data } = await getSysparameterByNames({
        parameterNames: ['TechAssistanceServicesInventoryFlow'],
      });
      this.isInventoryFlowEnabled =
        data[0].staticValue?.toLowerCase() === 'true';
    },
    async getBoardInfo(equipments = [], services = []) {
      const { data } = await referralService.getReferralSummary(
        this.referralId
      );

      this.referral = {
        ...data,
        equipments,
        services,
        record: false,
      };

      this.setReadOnly();
    },
    async getClassifications() {
      const { data } = await referralService.getServiceClassifications();
      this.classifications = data;
    },
    async getPurchaseMethods() {
      const { data } = await referralService.getEquipmentPurchaseMethods();
      this.purchaseMethods = data;
    },
    async getServiceDocumentationMethods() {
      const { data } = await referralService.getServiceDocumentationMethods();
      this.serviceDocumentationMethods = data;
    },
    async handleSave() {
      const comp = 'referral-services';
      const observer = 'referral-services-observer';
      const valid = await this.$refs[comp]?.$refs[observer]?.validate();
      if (!valid) {
        this.ShowToast(
          'Error',
          'Complete los campos requeridos para guardar los Servicios.',
          'error'
        );
        return;
      }
      if (this.isInventoryFlowEnabled) {
        const inventoryValid = this.validateInventoryFlow();
        if (!inventoryValid) return;
      }
      try {
        const { equipments, services } = this.referral;
        const data = services.map((x) => ({
          recommendationEquipmentId: x.equipment.id,
          peiAtClassificationServiceId: x.classification?.id,
          purchaseMethod: x.purchaseMethod?.id,
          isProceed: x.isProceed,
          isRequiredReplacement: x.isRequiredReplacement,
          isNew: x.isNew,
          isUsed: x.isUsed,
          isAuction: x.isAuction,
        }));

        const isProceeds = data.filter((x) => x.isProceed);
        const status = data.every((x) => x.isProceed === false)
          ? 'Ajustado'
          : isProceeds.every(
              (x) => x.purchaseMethod === PurchaseMethods.Inventory
            )
          ? 'Pendiente de Entrega Escuela'
          : isProceeds.some((x) => x.purchaseMethod === PurchaseMethods.SIFDE)
          ? 'Pendiente de Validación'
          : 'Pendiente de Servicio';
        if (this.referral.serviceStatusName !== status) {
          const confirmed = await this.ShowCancelModal(
            'Confirmación',
            `Está seguro que desea cambiar el estatus del referido de <b>${this.referral.serviceStatusName}</b> a <b>${status}</b>?`
          );
          if (!confirmed) return;
        }

        await referralService.saveReferralServices(this.referralId, data);

        await this.getBoardInfo(equipments, services);
        this.ShowToast(
          'Éxito',
          'Información ha sido guardada exitosamente.',
          'success'
        );
        await this.$refs['referral-services'].getServices();
      } catch (error) {
        this.ShowToast('Error', error.response?.data?.message, 'error');
      }
    },
    validateInventoryFlow() {
      this.validate = true;
      const { services } = this.referral;

      const allDocumented = services
        .filter(
          (x) =>
            x.referral.referralTypeId !=
              PeiAtReferralTypes.TechnicalAssistanceServiceRequest &&
            x.isRequiredReplacement
        )
        .every((x) => x.requestedBySifde || x.inventoryEquipment);
      if (!allDocumented) {
        this.ShowToast(
          'Error',
          'Necesita completar la documentación del método de compra.',
          'error'
        );
        return false;
      }
      const hasPendingTransferRequests = services.some(
        (x) => x.inventoryEquipment?.hasPendingTransferRequest
      );
      if (hasPendingTransferRequests) {
        this.ShowToast(
          'Error',
          'Actualmente al menos uno de los equipos se encuentra con una solicitud de transferencia en proceso.',
          'error'
        );
        return false;
      }
      return true;
    },
    onEquipmentsLoaded() {
      this.showEquipments =
        Boolean(this.referral.equipments.length) &&
        this.type === PeiAtReferralTypeTabs.Equipment;
      this.getBoardInfo(this.referral.equipments);
    },
    async onSaveServiceDocumentation() {
      const { equipments, services } = this.referral;
      await this.getBoardInfo(equipments, services);
    },
    setReadOnly() {
      const { services } = this.referral;
      if (!services || !services.length) {
        this.serviceAreSaved = false;
        return;
      }
      const valid = services.every((x) => x.originalIsProceed !== null);
      this.serviceAreSaved = valid;
    },
  },
};
</script>
<style lang="scss"></style>
